import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Route as RouteIcon, RefreshCw } from 'lucide-react';
import KanbanBoard from '@/features/pedidos/KanbanBoard';
import PedidoDetailDrawer from '@/features/pedidos/PedidoDetailDrawer';
import HistoricoSection from '@/features/pedidos/HistoricoSection';
import DateNav from '@/features/pedidos/DateNav';
import { usePedidosStore, ordenarPedidos, dataHojeISO } from '@/features/pedidos/store';
import { gerarRota } from '@/features/pedidos/api';
import Input from '@/components/Input';
import Button from '@/components/Button';
import Numeric from '@/components/Numeric';
import { useToast } from '@/components/Toast';

export default function KanbanPage() {
  const pedidosMap = usePedidosStore((s) => s.pedidos);
  const dataAtual = usePedidosStore((s) => s.data);
  const todos = useMemo(() => ordenarPedidos(pedidosMap), [pedidosMap]);
  const carregar = usePedidosStore((s) => s.carregar);
  const carregando = usePedidosStore((s) => s.carregando);
  const [filtro, setFiltro] = useState('');
  const [gerando, setGerando] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();

  const visiveisCount = useMemo(() => {
    const t = filtro.trim().toLowerCase();
    if (!t) return todos.length;
    return todos.filter((p) => {
      const hay = `${p.nome_cliente} ${p.numero_ticket} ${p.endereco_entrega}`.toLowerCase();
      return hay.includes(t);
    }).length;
  }, [todos, filtro]);

  const ativos = todos.filter((p) => ['recebido', 'pago', 'em_separacao'].includes(p.status));
  const isHoje = dataAtual === dataHojeISO();

  async function onGerarRota() {
    setGerando(true);
    try {
      const resp = await gerarRota(dataAtual === 'all' ? undefined : dataAtual);
      if (!resp.rota) {
        const aviso = resp.aviso || 'Nenhum pedido elegível para a rota.';
        toast('info', aviso);
        if (resp.diagnostico?.enderecos_falha?.length) {
          // mensagem extra com os primeiros tickets que falharam
          const falhas = resp.diagnostico.enderecos_falha
            .map((f) => f.numero_ticket)
            .join(', ');
          toast('error', `Sem coords: ${falhas}. Edite o endereço.`);
        }
      } else {
        toast(
          'success',
          `Rota traçada — ${resp.rota.rota_otimizada.length} paradas, ${(
            (resp.rota.distancia_total_m ?? 0) / 1000
          ).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} km.`,
        );
        navigate('/rotas');
      }
    } catch (err) {
      toast('error', `Falha ao gerar rota — ${(err as Error).message}.`);
    } finally {
      setGerando(false);
    }
  }

  return (
    <div className="flex flex-col gap-s7 h-full">
      <header className="flex items-end justify-between gap-s5 flex-wrap">
        <div>
          <h1 className="t-overline mb-s3">{isHoje ? 'Pedidos do dia' : 'Pedidos'}</h1>
          <p className="font-display text-[40px] leading-none text-fg-1 tracking-[-0.02em]">
            <Numeric display size="lg">
              {ativos.length}
            </Numeric>{' '}
            <span className="text-fg-3 italic font-display text-[28px]">
              {ativos.length === 1 ? 'pedido em movimento' : 'pedidos em movimento'}
            </span>
          </p>
        </div>

        <div className="flex items-center gap-s3 flex-wrap justify-end">
          <DateNav />
          <div className="w-[280px]">
            <Input
              search
              placeholder="Buscar cliente, ticket, endereço…"
              value={filtro}
              onChange={(e) => setFiltro(e.target.value)}
            />
          </div>
          <Button
            variant="default"
            onClick={() => carregar()}
            disabled={carregando}
            leading={<RefreshCw size={14} strokeWidth={1.6} className={carregando ? 'animate-spin' : ''} />}
          >
            Recarregar
          </Button>
          <Button
            variant="primary"
            onClick={onGerarRota}
            disabled={gerando}
            leading={<RouteIcon size={14} strokeWidth={1.6} />}
          >
            {gerando ? 'Gerando…' : 'Gerar rota'}
          </Button>
        </div>
      </header>

      {filtro && (
        <p className="font-body text-[12px] text-fg-3 -mt-s5">
          {visiveisCount} de {todos.length} pedidos correspondem a "{filtro}".
        </p>
      )}

      <KanbanBoard filtro={filtro} />
      <HistoricoSection />
      <PedidoDetailDrawer />

      {todos.length === 0 && !carregando && (
        <p className="font-display italic text-[24px] text-fg-3 text-center mt-s10">
          {dataAtual === 'all' ? 'O jardim está em silêncio.' : `Sem pedidos em ${dataAtual}.`}
        </p>
      )}
    </div>
  );
}
